import { accents, gray } from 'sutra/tokens/colors'

import { ENTITY_LABELS } from 'src/components/agent-forms/benchmark-entity-labels'
import type { BenchmarkingEntityType } from 'src/components/agent-forms/BenchmarkingAgentConfigForm'
import type { BenchmarkingGroupStats } from 'src/components/agent-forms/use-benchmark-compare-stats'

export type BenchmarkStatKey = keyof BenchmarkingGroupStats

export interface BenchmarkStatSlot {
  key: BenchmarkStatKey
  label: string
  /** Averages get the focus-vs-universe delta dot; raw counts don't — the
   * focus is a subset of the universe by construction, so a count is always
   * "lower" and a dot on it would say nothing. */
  compared: boolean
  /** Singular form to substitute when the slot's value is exactly 1 (e.g.
   * "Brand" for a `brand_count` of 1). Unset for slots with no singular form
   * (Records, Avg sentiment). */
  singularLabel?: string
}

const ENTITY_COUNT_SLOT: Record<BenchmarkingEntityType, BenchmarkStatSlot> = {
  brand: {
    key: 'brand_count',
    label: 'Brands',
    compared: false,
    singularLabel: ENTITY_LABELS.brand.singular,
  },
  product_hierarchy: {
    key: 'product_count',
    label: 'Products',
    compared: false,
    singularLabel: ENTITY_LABELS.product_hierarchy.singular,
  },
}

/** The selected entity's count → Records → Avg sentiment — this is what lets
 * every caption render immediately, with only the number skeletoned while the
 * request is in flight. `entityType === null` (reachable only pre-hydration
 * on the edit/staff surfaces) omits the leading slot rather than guessing an
 * entity. `sentence_count` / `avg_rating` / the unselected entity count are
 * still in `BenchmarkingGroupStats` — they're just not surfaced here. */
export function footerStatSlots(
  entityType: BenchmarkingEntityType | null,
): BenchmarkStatSlot[] {
  const entitySlot = entityType ? ENTITY_COUNT_SLOT[entityType] : null
  return [
    ...(entitySlot ? [entitySlot] : []),
    { key: 'record_count', label: 'Records', compared: false },
    { key: 'avg_sentiment', label: 'Avg sentiment', compared: true },
  ]
}

/** Every field zeroed/nulled — rendered on the Focus card's summary row
 * before any brand/product is picked, so the row reads as "nothing focused
 * yet" rather than either a loading state or the universe's own numbers. */
export const ZERO_STATS: BenchmarkingGroupStats = {
  record_count: 0,
  sentence_count: 0,
  product_count: 0,
  brand_count: 0,
  avg_rating: null,
  avg_sentiment: null,
}

export function formatBenchmarkStat(
  slot: BenchmarkStatSlot,
  stats: BenchmarkingGroupStats,
): string {
  if (slot.key === 'avg_sentiment') {
    const value = stats.avg_sentiment
    if (value === null) return '—'
    const sign = value > 0 ? '+' : ''
    return `${sign}${value.toFixed(2)}`
  }
  if (slot.key === 'avg_rating') {
    return stats.avg_rating !== null ? `${stats.avg_rating.toFixed(1)}/5` : '—'
  }
  return stats[slot.key].toLocaleString()
}

export type BenchmarkDeltaTone = 'higher' | 'lower' | 'even' | 'unknown'

export function benchmarkDeltaTone(
  focus: number | null,
  universe: number | null,
): BenchmarkDeltaTone {
  if (focus === null || universe === null) return 'unknown'
  if (focus > universe) return 'higher'
  if (focus < universe) return 'lower'
  return 'even'
}

export const BENCHMARK_DELTA_COLOR: Record<BenchmarkDeltaTone, string> = {
  higher: accents.emeraldAura[500],
  lower: accents.crimsonBloom[500],
  even: gray[400],
  unknown: gray[400],
}

export function benchmarkDeltaTooltip(
  tone: BenchmarkDeltaTone,
  universeText: string,
): string {
  switch (tone) {
    case 'higher':
      return `Higher than the universe (${universeText}).`
    case 'lower':
      return `Lower than the universe (${universeText}).`
    case 'even':
      return `Same as the universe (${universeText}).`
    case 'unknown':
      return 'No comparison available.'
  }
}
